import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import SearchBar from '../../components/search-bar/SearchBar';
import CompactSearchBar from '../../components/search-bar/CompactSearchBar';
import LoadingSpinner from '../../components/loading-spinner/LoadingSpinner';
import Header from '../../components/header/Header';
import {
  ListingIntent,
  PropertyType,
} from '../../components/property/property-card/PropertyCardTypes';
import ShareModal from '../../components/property/property-card/ShareModal';
import {
  SearchResultsContent,
  useSearchListings,
  useBookmarks,
  useShare,
  useSearchAnalytics,
} from './components';
import { useMobileDetection } from '../../hooks/useMobileDetection';
import { remoteConfig } from '../../utility/firebaseRemoteConfigHelper';
import { useAnalyticsWithConsent } from '../../hooks/useAnalyticsWithConsent';
import { useAuth } from '../../auth/firebaseAuth';
import {
  parseUrlSlugs,
  generateSEOTitle,
  generateSEODescription,
  generateSEOH1,
  generateCanonicalUrl,
  generateStructuredData,
} from '../../utility/pSEO/slugParser';

export interface SearchParams {
  location: string;
  minPrice: number;
  maxPrice: number;
  bedrooms: number;
  bathrooms: number;
  propertyType: PropertyType | 'Any';
  berRating: string;
  availableFrom: Date | null;
  listingIntent: ListingIntent | 'Any';
}

interface BrowseListingsPageProps {
  initialSearchParams?: Partial<SearchParams>;
}

const BrowseListingsPage: React.FC<BrowseListingsPageProps> = ({ initialSearchParams }) => {
  // Get URL parameters for pSEO (e.g., /rent/dublin/pet-friendly)
  const params = useParams();

  // Feature flags and device detection
  const isMobile = useMobileDetection();
  const isNewMobileUXEnabled = remoteConfig.isNewMobileUXActivated();
  const shouldUseMobileUX = isMobile && isNewMobileUXEnabled;
  const analytics = useAnalyticsWithConsent();
  const { currentUser } = useAuth();

  // Parse URL slugs to extract filter parameters (memoized to prevent infinite re-renders)
  const urlSlugParams = useMemo(() => {
    // React Router v6 wildcard param uses the key "*" and contains the remaining path.
    // Split any wildcard segment by "/" to correctly parse multi-segment pSEO slugs
    // e.g. /sale/galway/apartment/200000-300000 → ["sale", "galway", "apartment", "200000-300000"]
    const pathSegments = Object.values(params)
      .filter(Boolean)
      .flatMap((value) => value!.split('/'));
    return parseUrlSlugs(pathSegments);
  }, [params]);

  // Combine initial params with URL slug params (memoized to prevent infinite re-renders)
  const mergedInitialParams = useMemo(() => {
    return {
      ...initialSearchParams,
      ...urlSlugParams,
    };
  }, [initialSearchParams, urlSlugParams]);

  console.log('Merged Initial Params:', mergedInitialParams);

  // Custom hooks for different functionalities
  const {
    listings,
    isLoading,
    currentPage,
    totalListings,
    sortOption,
    searchParams,
    totalPages,
    setSearchParams,
    handleSearch,
    handleSort,
    handlePageChange,
  } = useSearchListings(mergedInitialParams);

  const { bookmarkedListings, handleBookmarkToggle } = useBookmarks();

  const { isShareModalOpen, currentShareUrl, currentShareTitle, setIsShareModalOpen, handleShare } =
    useShare();

  const { handleListingView, trackSearchResults, trackSessionEnd } = useSearchAnalytics();

  // Memoize SEO content generation to prevent re-renders
  const canonicalUrl = useMemo(() => {
    return generateCanonicalUrl(
      Object.keys(params)
        .filter((key) => params[key as keyof typeof params])
        .map((key) => params[key as keyof typeof params]!)
    );
  }, [params]);

  const seoContent = useMemo(() => {
    return {
      title: generateSEOTitle(searchParams, searchParams.listingIntent as ListingIntent | 'Any'),
      description: generateSEODescription(
        searchParams,
        searchParams.listingIntent as ListingIntent | 'Any'
      ),
      h1: generateSEOH1(searchParams, searchParams.listingIntent as ListingIntent | 'Any'),
    };
  }, [searchParams]);

  const structuredData = useMemo(() => {
    return generateStructuredData(searchParams, totalListings);
  }, [searchParams, totalListings]);

  const shouldShowDublinCommuterWidget = useMemo(() => {
    const normalizedLocation = String(searchParams.location || '').toLowerCase().trim();
    const isDublinLocation =
      normalizedLocation === 'dublin' ||
      normalizedLocation.startsWith('dublin ') ||
      normalizedLocation.startsWith('dublin-');
    const isRentIntent =
      searchParams.listingIntent === 'Any' || searchParams.listingIntent === 'rent';
    return isDublinLocation && isRentIntent;
  }, [searchParams.location, searchParams.listingIntent]);

  // Track search results when they change
  useEffect(() => {
    if (!isLoading) {
      trackSearchResults(totalListings, searchParams, sortOption);

      // Track pixel page view for browse listings
      analytics.trackPixelPageView({
        ...(currentUser && {
          email: currentUser.email || undefined,
          externalId: currentUser.uid,
        }),
      });
    }
  }, [
    totalListings,
    searchParams,
    sortOption,
    isLoading,
    trackSearchResults,
    analytics,
    currentUser,
  ]);

  // Track session end on unmount
  useEffect(() => {
    return () => {
      trackSessionEnd(bookmarkedListings);
    };
  }, [trackSessionEnd, bookmarkedListings]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [currentPage]);

  // Enhanced handlers with analytics
  const handleBookmarkToggleWithAnalytics = (id: number) => {
    handleBookmarkToggle(id, currentPage, listings);
  };

  const handleShareWithAnalytics = (listing: any) => {
    handleShare(listing, currentPage, listings);
  };

  const handleListingViewWithAnalytics = (listing: any) => {
    handleListingView(listing, currentPage, listings, searchParams);
  };

  // SEO Helmet block - Dynamic SEO for pSEO
  const seoKeywords = [
    'property listings',
    'Ireland',
    searchParams.location || 'Dublin',
    searchParams.propertyType || 'apartments',
    'rental properties',
    searchParams.listingIntent === 'sale' ? 'homes for sale' : 'properties for rent',
  ].join(', ');

  // Track search results when they change
  useEffect(() => {
    if (!isLoading) {
      trackSearchResults(totalListings, searchParams, sortOption);

      // Track pixel page view for browse listings
      analytics.trackPixelPageView({
        ...(currentUser && {
          email: currentUser.email || undefined,
          externalId: currentUser.uid,
        }),
      });
    }
  }, [
    totalListings,
    searchParams,
    sortOption,
    isLoading,
    trackSearchResults,
    analytics,
    currentUser,
  ]);

  // Track session end on unmount
  useEffect(() => {
    return () => {
      trackSessionEnd(bookmarkedListings);
    };
  }, [trackSessionEnd, bookmarkedListings]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <Helmet>
        <title>{seoContent.title}</title>
        <meta name="description" content={seoContent.description} />
        <meta name="keywords" content={seoKeywords} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={seoContent.title} />
        <meta property="og:description" content={seoContent.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />

        {/* Structured Data for Search Engines */}
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      <div className={`container mx-auto ${shouldUseMobileUX ? 'px-3 py-4' : 'px-4 py-8'}`}>
        <Header hideOnMobile={shouldUseMobileUX} />

        {/* Dynamic H1 for pSEO */}
        <h1 className="sr-only">{seoContent.h1}</h1>

        {shouldUseMobileUX ? (
          <CompactSearchBar
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            onSearch={handleSearch}
          />
        ) : (
          <SearchBar
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            onSearch={handleSearch}
          />
        )}

        <div className="text-sm text-gray-500 mt-4 mb-2 text-center md:text-left">
          <p>
            Results are sorted by 'Newest First' by default. 'Featured' listings are paid promotions
            and will be clearly marked.
          </p>
        </div>

        <SearchResultsContent
          listings={listings}
          totalListings={totalListings}
          searchParams={searchParams}
          sortOption={sortOption}
          currentPage={currentPage}
          totalPages={totalPages}
          isLoading={isLoading}
          bookmarkedListings={bookmarkedListings}
          isMobileCompactView={shouldUseMobileUX}
          showDublinCommuterWidget={shouldShowDublinCommuterWidget}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onBookmarkToggle={handleBookmarkToggleWithAnalytics}
          onShare={handleShareWithAnalytics}
          onListingView={handleListingViewWithAnalytics}
        />

        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          url={currentShareUrl}
          title={currentShareTitle}
        />
      </div>
    </>
  );
};

export default BrowseListingsPage;
